import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { gsap } from 'gsap';
import { LoadingScreen, ErrorScreen } from '../hooks/useApiLoading';
import { useNotificationHook } from '../hooks/useNotificationHook';
import { 
  Globe, Users, Download, Star, Search, Filter, 
  Heart, Eye, ShoppingCart, ArrowRight, Loader2, AlertCircle,
  Award, Zap, CheckCircle, Palette, Code, Smartphone, ExternalLink,
  ChevronLeft, ChevronRight, Video, FileText, Tag, TrendingUp
} from 'lucide-react';
import { productAPI, productsPageAPI, promotionsAPI } from '../services/api';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import WishlistButton from '../components/WishlistButton';
import SharePrintButtons from '../components/SharePrintButtons';
import { constructApiUrl, getS3ImageUrl } from '../utils/apiUtils';
import { debounce } from 'lodash';

const Products = () => {
  const { categorySlug } = useParams();
  const { addToCart, isInWishlist, addToWishlist, removeFromWishlist } = useCart();
  const { formatPrice } = useCurrency();
  const { notify } = useNotificationHook();
  const [selectedCategory, setSelectedCategory] = useState(categorySlug || 'all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('popular');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Banner promotions state
  const [promotions, setPromotions] = useState([]);
  const [topBannerPromotions, setTopBannerPromotions] = useState([]);
  const [topBannerIndex, setTopBannerIndex] = useState(0);
  
  // Products page content state
  const [pageContent, setPageContent] = useState({
    main_title: 'Digital',
    gradient_title: 'Products',
    main_description: 'Premium themes, plugins, and source codes for developers and businesses',
    stats_total_products: 7,
    stats_happy_customers: 5000,
    stats_downloads: 15000,
    stats_average_rating: 4.8,
    features_title: 'Why Choose Our Products?',
    features_description: 'Premium quality digital products with comprehensive support and regular updates',
    features_items: [],
    more_title: 'Need Custom Development?',
    more_description: 'Can\'t find what you\'re looking for? We offer custom development services tailored to your specific requirements.',
    more_button1_text: 'Get Custom Quote',
    more_button1_link: '/contact',
    more_button2_text: 'View Services',
    more_button2_link: '/services'
  });
  
  const productsRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Map frontend sort values to backend sort parameters
  const getSortParams = (sortBy) => {
    switch (sortBy) {
      case 'price-low':
        return { sortBy: 'regular_license_price', sortOrder: 'ASC' };
      case 'price-high':
        return { sortBy: 'regular_license_price', sortOrder: 'DESC' };
      case 'rating':
        return { sortBy: 'rating', sortOrder: 'DESC' };
      case 'downloads':
        return { sortBy: 'downloads', sortOrder: 'DESC' };
      case 'popular':
      default:
        return { sortBy: 'featured', sortOrder: 'DESC' };
    }
  };

  // Initialize from URL params
  useEffect(() => {
    const initialSearch = searchParams.get('q') || '';
    if (initialSearch) setSearchTerm(initialSearch);
  }, []);

  // Update selectedCategory when categorySlug changes
  useEffect(() => {
    setSelectedCategory(categorySlug || 'all');
  }, [categorySlug]);

  // Fetch promotions for products page
  useEffect(() => {
    const fetchPromotions = async () => {
      try {
        const response = await promotionsAPI.getAll({
          page: 'products'
        });
        
        if (response?.data?.success) {
          const activePromotions = response.data.data.promotions?.filter(promotion => {
            // Check if promotion is active
            if (!promotion.is_active) return false;
            
            // Check if promotion is for products page
            if (!promotion.show_on_pages?.includes('products')) return false;
            
            // Allow all promotion types for products page
            // (Top banner will filter to only banner/coupon, bottom section will show all types)
            
            // Check validity dates
            const now = new Date();
            if (promotion.valid_from && new Date(promotion.valid_from) > now) return false;
            if (promotion.valid_until && new Date(promotion.valid_until) < now) return false;
            
            return true;
          }) || [];
          
          setPromotions(activePromotions);
          
          // Filter promotions for top banner (only banner and coupon types)
          const topBannerPromos = activePromotions.filter(promotion => 
            ['banner', 'coupon'].includes(promotion.type)
          );
          setTopBannerPromotions(topBannerPromos);
        }
      } catch (err) {
        console.error('Error fetching promotions:', err);
      }
    };

    fetchPromotions();
  }, []);

  // Auto-scroll for top banner promotions
  useEffect(() => {
    if (topBannerPromotions.length <= 1) return;

    const topBannerInterval = setInterval(() => {
      setTopBannerIndex(prev => (prev + 1) % topBannerPromotions.length);
    }, 5000); // Change every 5 seconds

    return () => {
      clearInterval(topBannerInterval);
    };
  }, [topBannerPromotions.length]);

  // Fetch products page content
  const fetchPageContent = async () => {
    try {
      console.log('🔄 Fetching products page content...');
      const response = await productsPageAPI.getContent();
      console.log('📡 Products page API response:', response);
      if (response?.data?.success) {
        console.log('✅ Products page content loaded:', response.data.data);
        setPageContent(response.data.data);
      } else {
        console.log('❌ Products page API response not successful:', response);
      }
    } catch (err) {
      console.error('❌ Error fetching products page content:', err);
    }
  };

  // Fetch categories with counts
  const fetchCategories = async () => {
    try {
      const response = await fetch(constructApiUrl('product-categories?status=active'));
      
      // Check if response is ok before parsing JSON
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Response is not JSON');
      }
      
      const categoriesData = await response.json();
      if (categoriesData.success) {
        // Get all products for accurate counts
        const allProductsResponse = await productAPI.getAll({ status: 'active' });
        const allProducts = allProductsResponse?.data?.data?.products || [];
        
        const categoriesWithCounts = [
          { id: 'all', name: 'All Products', icon: Globe, count: allProducts.length },
          ...categoriesData.data.categories.map(cat => ({
            id: cat.slug,
            name: cat.name,
            count: allProducts.filter(p => p.category === cat.slug).length,
            icon: getCategoryIcon(cat.slug)
          }))
        ];
        setCategories(categoriesWithCounts);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  // Fetch products from API
  const fetchProducts = async (page = 1, append = false) => {
    try {
      if (!append) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);
      
      const sortParams = getSortParams(sortBy);
      
      const response = await productAPI.getAll({
        status: 'active',
        category: categorySlug || undefined,
        page: page,
        limit: 12,
        ...sortParams
      });
      
      if (response?.data?.success) {
        const productsData = response.data.data.products || [];
        const paginationData = response.data.data.pagination || {};
        
        // Parse JSON fields and format data
        const formattedProducts = productsData.map(product => {
          try {
            return {
              ...product,
              tags: typeof product.tags === 'string' ? JSON.parse(product.tags) : product.tags || [],
              gallery: typeof product.gallery === 'string' ? JSON.parse(product.gallery) : product.gallery || [],
              features: typeof product.features === 'string' ? JSON.parse(product.features) : product.features || [],
              featured: Boolean(product.featured),
              // Format price - use regular license price
              formattedPrice: formatPrice(product.regular_license_price || 0),
              formattedOriginalPrice: product.regular_license_original_price ? 
                formatPrice(product.regular_license_original_price) : null,
              // Calculate discount percentage
              discountPercentage: product.regular_license_original_price ? 
                Math.round(((product.regular_license_original_price) - (product.regular_license_price || 0)) / (product.regular_license_original_price) * 100) : 0
            };
          } catch (parseError) {
            console.error('Error parsing product:', product, parseError);
            return {
              ...product,
              tags: [],
              gallery: [],
              features: [],
              featured: Boolean(product.featured),
              formattedPrice: formatPrice(product.regular_license_price || 0),
              formattedOriginalPrice: product.regular_license_original_price ? 
                formatPrice(product.regular_license_original_price) : null,
              discountPercentage: product.regular_license_original_price ? 
                Math.round(((product.regular_license_original_price) - (product.regular_license_price || 0)) / (product.regular_license_original_price) * 100) : 0
            };
          }
        });
        
        if (append) {
          setProducts(prev => [...prev, ...formattedProducts]);
        } else {
          setProducts(formattedProducts);
        }
        
        setCurrentPage(page);
        setHasMoreProducts(page < paginationData.pages);
      } else {
        console.error('API response not successful:', response);
        setError('Failed to fetch products');
        notify.error('Failed to fetch products', 'Loading Error');
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(`Error loading products: ${err.message}`);
      notify.error(`Error loading products: ${err.message}`, 'Network Error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    const iconMap = {
      'wordpress-themes': Palette,
      'plugins': Code,
      'source-codes': Code,
      'licenses': Globe,
      'themes': Palette,
      'mobile': Smartphone,
      'codes': Code,
      'templates': Globe,
      'libraries': Code
    };
    return iconMap[category] || Globe;
  };

  // Get feature icon
  const getFeatureIcon = (iconName) => {
    const iconMap = {
      'CheckCircle': CheckCircle,
      'RefreshCw': Zap,
      'Headphones': Users,
      'Shield': CheckCircle,
      'Star': Star,
      'Download': Download,
      'ShoppingBag': ShoppingCart,
      'Code': Code,
      'Zap': Zap,
      'Globe': Globe,
      'Award': Award,
      'Palette': Palette,
      'Smartphone': Smartphone
    };
    return iconMap[iconName] || CheckCircle;
  };

  // Load more products handler
  const handleLoadMore = () => {
    if (!loadingMore && hasMoreProducts) {
      fetchProducts(currentPage + 1, true);
    }
  };

  // Refetch products when sort order changes
  useEffect(() => {
    if (products.length > 0) {
      setCurrentPage(1);
      setProducts([]);
      fetchProducts(1, false);
    }
  }, [sortBy]);

  useEffect(() => {
    // Reset pagination when category changes
    setCurrentPage(1);
    setProducts([]);
    setHasMoreProducts(false);
    
    fetchProducts(1, false);
    fetchCategories(); // Fetch categories after products
    fetchPageContent(); // Fetch page content
    
    // Set up periodic refresh of page content (every 30 seconds)
    const interval = setInterval(() => {
      fetchPageContent();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [categorySlug]);

  // Refresh content when component mounts
  useEffect(() => {
    fetchPageContent();
  }, []);

  // Refresh content when user navigates to the page
  useEffect(() => {
    const handleFocus = () => {
      fetchPageContent();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  useEffect(() => {
    // Set initial states
    gsap.set('.products-title', { opacity: 0, y: 50 });
    gsap.set('.products-subtitle', { opacity: 0, y: 30 });
    gsap.set('.filter-bar', { opacity: 0, y: 20 });
    // Only run animations if elements exist
    const productsTitle = document.querySelector('.products-title');
    const productsSubtitle = document.querySelector('.products-subtitle');
    const filterBar = document.querySelector('.filter-bar');
    const productCards = document.querySelectorAll('.product-card');

    if (productCards.length > 0) {
      gsap.set('.product-card', { opacity: 0, y: 40 });
    }

    // Animate elements only if they exist
    const tl = gsap.timeline({ delay: 0.2 });
    
    if (productsTitle) {
      tl.to('.products-title', { duration: 0.8, opacity: 1, y: 0, ease: 'power3.out' });
    }
    
    if (productsSubtitle) {
      tl.to('.products-subtitle', { duration: 0.8, opacity: 1, y: 0, ease: 'power3.out' }, '-=0.5');
    }
    
    if (filterBar) {
      tl.to('.filter-bar', { duration: 0.6, opacity: 1, y: 0, ease: 'power3.out' }, '-=0.3');
    }
    
    if (productCards.length > 0) {
      tl.to('.product-card', { duration: 0.6, opacity: 1, y: 0, stagger: 0.1, ease: 'power3.out' }, '-=0.3');
    }

    return () => {
      tl.kill();
    };
  }, [products]); // Re-run animation when products change

  // Search functionality - trigger API call when searching
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [hasMoreSearchResults, setHasMoreSearchResults] = useState(false);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (searchTerm, page = 1, append = false) => {
      if (!searchTerm.trim()) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      try {
        setIsSearching(true);
        // Clear previous results only when starting a new search (not appending)
        if (!append) {
          setSearchResults([]);
        }
        
        const sortParams = getSortParams(sortBy);
        
        const response = await productAPI.getAll({
          status: 'active',
          category: categorySlug || undefined,
          search: searchTerm,
          page: page,
          limit: 12,
          ...sortParams
        });
        
        if (response?.data?.success) {
          const productsData = response.data.data.products || [];
          const paginationData = response.data.data.pagination || {};
          
          // Parse JSON fields and format data
          const formattedProducts = productsData.map(product => {
            try {
              return {
                ...product,
                tags: typeof product.tags === 'string' ? JSON.parse(product.tags) : product.tags || [],
                gallery: typeof product.gallery === 'string' ? JSON.parse(product.gallery) : product.gallery || [],
                features: typeof product.features === 'string' ? JSON.parse(product.features) : product.features || [],
                featured: Boolean(product.featured),
                formattedPrice: formatPrice(product.regular_license_price || 0),
                formattedOriginalPrice: product.regular_license_original_price ? 
                  formatPrice(product.regular_license_original_price) : null,
                discountPercentage: product.regular_license_original_price ? 
                  Math.round(((product.regular_license_original_price) - (product.regular_license_price || 0)) / (product.regular_license_original_price) * 100) : 0
              };
            } catch (parseError) {
              console.error('Error parsing product:', product, parseError);
              return {
                ...product,
                tags: [],
                gallery: [],
                features: [],
                featured: Boolean(product.featured),
                formattedPrice: formatPrice(product.regular_license_price || 0),
                formattedOriginalPrice: product.regular_license_original_price ? 
                  formatPrice(product.regular_license_original_price) : null,
                discountPercentage: product.regular_license_original_price ? 
                  Math.round(((product.regular_license_original_price) - (product.regular_license_price || 0)) / (product.regular_license_original_price) * 100) : 0
              };
            }
          });
          
          if (append) {
            setSearchResults(prev => [...prev, ...formattedProducts]);
          } else {
            setSearchResults(formattedProducts);
          }
          
          setSearchPage(page);
          setHasMoreSearchResults(page < paginationData.pages);
        }
      } catch (err) {
        console.error('Error searching products:', err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    [sortBy, categorySlug, formatPrice]
  );

  // Handle search term changes
  useEffect(() => {
    if (searchTerm.trim()) {
      setSearchPage(1);
      setHasMoreSearchResults(false);
      // Don't clear search results immediately - let the debounced search handle it
      debouncedSearch(searchTerm, 1, false);
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }
  }, [searchTerm, debouncedSearch]);

  // Keep search term in the URL for shareable links
  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString());
    if (searchTerm && searchTerm.trim().length > 0) {
      params.set('q', searchTerm);
    } else {
      params.delete('q');
    }
    // Avoid unnecessary updates
    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params);
    }
  }, [searchTerm, setSearchParams]);

  // Load more search results
  const handleLoadMoreSearch = () => {
    if (!isSearching && hasMoreSearchResults && searchTerm.trim()) {
      debouncedSearch(searchTerm, searchPage + 1, true);
    }
  };

  // Determine which products to display
  const displayProducts = searchTerm.trim() ? searchResults : products;
  const hasMoreToShow = searchTerm.trim() ? hasMoreSearchResults : hasMoreProducts;
  const isLoadingMore = searchTerm.trim() ? isSearching : loadingMore;

  // Products are already sorted by the server, so we just use displayProducts
  const sortedProducts = displayProducts;

  // Dynamic stats from API
  const stats = [
    { number: pageContent.stats_total_products, label: 'Total Products', suffix: '+', icon: Globe },
    { number: pageContent.stats_happy_customers, label: 'Happy Customers', suffix: '+', icon: Users },
    { number: pageContent.stats_downloads, label: 'Sales', suffix: '+', icon: TrendingUp },
    { number: pageContent.stats_average_rating, label: 'Average Rating', suffix: '/5', icon: Star }
  ];

  // Show loading screen when data is being fetched
  if (loading) {
    return <LoadingScreen message="Loading products..." />;
  }

  // Show error screen when there's an error
  if (error) {
    return <ErrorScreen error={error} onRetry={() => window.location.reload()} message="Unable to Load Products" />;
  }

  // Don't render content if no data is available
  if (!pageContent || !pageContent.main_title) {
    return <LoadingScreen message="Loading content..." />;
  }

  return (
    <div className="min-h-screen">
      {/* Modern Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-100/10 via-purple-100/10 to-pink-100/10 dark:from-blue-800/5 dark:via-purple-800/5 dark:to-pink-800/5"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="products-title text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              {pageContent.main_title} <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">{pageContent.gradient_title}</span>
            </h1>
            <p className="products-subtitle text-lg sm:text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
              {pageContent.main_description}
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
              {stats.map((stat, index) => {
                const IconComponent = stat.icon;
                return (
                  <div key={index} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl p-6 text-center shadow-xl border border-white/20 hover:scale-105 transition-all duration-500">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                      {stat.number}{stat.suffix}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      {stat.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Banner Promotions */}
      {topBannerPromotions.length > 0 && (
        <div className="bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="relative">
              {/* Current Promotion */}
              {topBannerPromotions[topBannerIndex] && (
                <div 
                  key={topBannerPromotions[topBannerIndex].id} 
                  className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 transition-all duration-500 ease-in-out"
                >
                  <div className="flex items-center gap-4">
                    {/* Standardized Image/Video Space */}
                    <div className="flex-shrink-0 w-16 h-16">
                      {/* Banner Image */}
                      {topBannerPromotions[topBannerIndex].type === 'banner' && topBannerPromotions[topBannerIndex].image_url && (
                        <img 
                          src={topBannerPromotions[topBannerIndex].image_url} 
                          alt={topBannerPromotions[topBannerIndex].title}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      )}
                      
                      {/* Coupon Code Thumbnail */}
                      {topBannerPromotions[topBannerIndex].type === 'coupon' && (
                        <div className="w-full h-full bg-green-600 rounded-lg flex items-center justify-center p-2">
                          <div className="text-center">
                            <div className="text-white font-mono font-bold text-sm">
                              {topBannerPromotions[topBannerIndex].coupon_code}
                            </div>
                            {topBannerPromotions[topBannerIndex].discount_percentage > 0 && (
                              <div className="text-white/90 text-xs mt-1">
                                {topBannerPromotions[topBannerIndex].discount_percentage}% OFF
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      {/* Standardized Title */}
                      <h3 className="text-white font-semibold text-lg mb-1">
                        {topBannerPromotions[topBannerIndex].title}
                      </h3>
                      
                      {/* Standardized Description */}
                      {topBannerPromotions[topBannerIndex].description && (
                        <p className="text-white/90 text-sm mb-2">
                          {topBannerPromotions[topBannerIndex].description}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex-shrink-0">
                      <Tag className="w-5 h-5 text-white/70" />
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Controls */}
              {topBannerPromotions.length > 1 && (
                <div className="absolute top-1/2 transform -translate-y-1/2 flex justify-between w-full px-4">
                  <button
                    onClick={() => setTopBannerIndex(prev => prev > 0 ? prev - 1 : topBannerPromotions.length - 1)}
                    className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setTopBannerIndex(prev => (prev + 1) % topBannerPromotions.length)}
                    className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}

              {/* Dots Indicator */}
              {topBannerPromotions.length > 1 && (
                <div className="flex justify-center mt-4 space-x-2">
                  {topBannerPromotions.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setTopBannerIndex(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === topBannerIndex ? 'bg-white' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modern Filter Bar */}
      <section className="filter-bar py-8 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-blue-900 dark:to-indigo-900 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-100/10 via-purple-100/10 to-pink-100/10 dark:from-blue-800/5 dark:via-purple-800/5 dark:to-pink-800/5"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
          <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 items-stretch lg:items-center lg:justify-start">
            {/* Search */}
            <div className="relative w-full lg:w-96 xl:w-[28rem] 2xl:w-[32rem]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white shadow-lg"
              />
            </div>

            {/* Categories */}
            <div className="flex flex-nowrap overflow-x-auto scrollbar-hide gap-2 -mx-1 px-1 w-full lg:w-auto">
              {categories.map((category) => {
                const IconComponent = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => {
                      if (category.id === 'all') {
                        navigate('/products');
                      } else {
                        navigate(`/products/${category.id}`);
                      }
                    }}
                    className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap ${
                      selectedCategory === category.id
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                        : 'bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-700 dark:text-gray-300 hover:scale-105 border border-white/20 shadow-lg'
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span className="hidden sm:inline">{category.name}</span>
                    <span className="sm:hidden">{(category.name || '').split(' ')[0]}</span>
                    <span className="text-xs bg-white/20 px-2 py-1 rounded-full ml-1">
                      {category.count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Sort */}
            <div className="flex items-center gap-2 w-full lg:w-auto">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 shadow-lg"
              >
                <option value="popular" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">Most Popular</option>
                <option value="price-low" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">Price: Low to High</option>
                <option value="price-high" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">Price: High to Low</option>
                <option value="rating" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">Highest Rated</option>
                <option value="downloads" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">Most Downloaded</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Modern Products Grid */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-purple-800 to-slate-800 opacity-20"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(loading || (searchTerm.trim() && isSearching && searchResults.length === 0)) ? (
              <div className="col-span-full text-center py-16">
                <Loader2 className="w-12 h-12 text-blue-600 dark:text-blue-400 animate-spin mx-auto" />
                <p className="text-lg text-white/90 mt-4">
                  {searchTerm.trim() ? 'Searching products...' : 'Loading products...'}
                </p>
              </div>
            ) : error ? (
              <div className="col-span-full text-center py-16">
                <AlertCircle className="w-12 h-12 text-red-500 dark:text-red-400 mx-auto" />
                <p className="text-lg text-red-600 dark:text-red-400 mt-4">{error}</p>
              </div>
            ) : sortedProducts.length === 0 ? (
              <div className="col-span-full text-center py-16">
                <Globe className="w-12 h-12 text-white/60 mx-auto" />
                <p className="text-lg text-white/90 mt-4">No products found matching your criteria.</p>
              </div>
            ) : (
              sortedProducts.map((product, index) => (
                <Link 
                  key={product.id} 
                  to={`/product/${product.slug}`}
                  className="product-card card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl hover:scale-105 transition-all duration-500 cursor-pointer border border-white/20 block"
                >
                  {/* Product Image */}
                  <div className="relative mb-6 overflow-hidden rounded-2xl">
                    <img 
                      src={getS3ImageUrl(product.image)} 
                      alt={product.name}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    
                    {/* Badge */}
                    {(product.badge || product.featured) && (
                      <div className="absolute top-3 right-3">
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          product.featured ? 'bg-orange-500 text-white' :
                          product.badge === 'Best Seller' ? 'bg-green-500 text-white' :
                          product.badge === 'New' ? 'bg-blue-500 text-white' :
                          product.badge === 'Premium' ? 'bg-purple-500 text-white' :
                          product.badge === 'Featured' ? 'bg-orange-500 text-white' :
                          'bg-gray-500 text-white'
                        }`}>
                          {product.featured ? 'Featured' : product.badge}
                        </span>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="absolute top-12 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <WishlistButton 
                        item={product} 
                        itemType="product" 
                        size="small" 
                      />
                      <SharePrintButtons 
                        item={product} 
                        itemType="product" 
                        size="small" 
                      />
                    </div>

                    {/* Discount */}
                    {product.discountPercentage > 0 && (
                      <div className="absolute bottom-3 left-3">
                        <span className="px-2 py-1 bg-red-500 text-white text-xs font-semibold rounded">
                          {product.discountPercentage}% OFF
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="space-y-4 p-6">
                    <div className="flex items-start justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white line-clamp-2 flex-1">
                        {product.name}
                      </h3>
                    </div>

                    <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-2">
                      {product.description}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1">
                      {product.tags.slice(0, 3).map((tag, tagIndex) => (
                        <span 
                          key={tagIndex}
                          className="px-2 py-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Rating & Stats */}
                    <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-300">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        <span>{product.rating}</span>
                        <span>({product.reviews_count || 0})</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <TrendingUp className="w-4 h-4" />
                        <span>{product.sales_count || product.downloads}</span>
                      </div>
                    </div>

                    {/* Author & Date */}
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>by {product.author}</span>
                      <span>v{product.version_number || product.version}</span>
                    </div>

                    {/* Price & Actions */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex flex-col gap-1">
                        <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                          {product.formattedPrice}
                        </span>
                        {product.formattedOriginalPrice && (
                          <span className="text-sm text-gray-500 line-through">
                            {product.formattedOriginalPrice}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        {product.demo_url && (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(product.demo_url, '_blank');
                            }}
                            className="px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-2xl transition-all duration-300 flex items-center justify-center gap-2 hover:scale-105"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Live Preview
                          </button>
                        )}
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            addToCart(product);
                            navigate('/checkout');
                          }}
                          className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:scale-105 text-white font-semibold rounded-2xl transition-all duration-300 flex items-center justify-center gap-2 shadow-lg"
                        >
                          <ShoppingCart className="w-4 h-4" />
                          Buy Now
                        </button>
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>

          {/* Load More */}
          {hasMoreToShow && (
            <div className="text-center mt-12">
              <button 
                onClick={searchTerm.trim() ? handleLoadMoreSearch : handleLoadMore}
                disabled={isLoadingMore}
                className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-10 py-4 rounded-2xl font-bold text-lg hover:scale-105 transition-all duration-500 flex items-center gap-3 mx-auto shadow-glow disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin" />
                    {searchTerm.trim() ? 'Searching...' : 'Loading More...'}
                  </>
                ) : (
                  <>
                    {searchTerm.trim() ? 'Load More Search Results' : 'Load More Products'}
                    <ArrowRight className="w-6 h-6" />
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Modern Features Section */}
      <section className="py-24 bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-blue-900 dark:to-indigo-900 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-100/10 via-purple-100/10 to-pink-100/10 dark:from-blue-800/5 dark:via-purple-800/5 dark:to-pink-800/5"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8">
              {pageContent.features_title}
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {pageContent.features_description}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {pageContent.features_items.map((feature, index) => {
              const IconComponent = getFeatureIcon(feature.icon);
              return (
                <div key={index} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 text-center hover:scale-105 transition-all duration-500 border border-white/20">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Modern CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-50 via-purple-50 to-indigo-50 dark:from-blue-600 dark:via-purple-600 dark:to-indigo-600 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-100/20 via-purple-100/20 to-indigo-100/20 dark:from-blue-700/20 dark:via-purple-700/20 dark:to-indigo-700/20"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
            {pageContent.more_title}
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 dark:text-white/90 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed">
            {pageContent.more_description}
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link 
              to={pageContent.more_button1_link} 
              className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-10 py-5 rounded-2xl font-bold text-lg hover:scale-105 transition-all duration-500 flex items-center justify-center gap-3 shadow-glow"
            >
              {pageContent.more_button1_text}
              <ArrowRight className="w-6 h-6" />
            </Link>
            <Link 
              to={pageContent.more_button2_link} 
              className="card-3d border-2 border-gray-900 dark:border-white text-gray-900 dark:text-white px-10 py-5 rounded-2xl font-bold text-lg hover:bg-gray-900 hover:text-white dark:hover:bg-white dark:hover:text-gray-900 hover:scale-105 transition-all duration-500 shadow-glow"
            >
              {pageContent.more_button2_text}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
